/**
 * Widget Registry
 *
 * Central registry for all CoPilot Guide widgets.
 * Handles registration and retrieval of widget components by name or alias.
 *
 * Type-safe widget props: Each widget receives WidgetProps from @tonnie/copilot.
 * The registry enforces WidgetProps compliance at registration time.
 */

import type { ComponentType } from 'react';
import { widgetRegistry as sharedWidgetRegistry } from '@tonnie/copilot';
import type { WidgetDisplayMode, WidgetProps } from '@tonnie/copilot';

// Journey Builder widgets
import { FamilySetupWidget, JourneySummaryWidget, ScenarioSelectorWidget } from './journey-builder';

// Checkup Assistant widgets
import {
  LanguageTestWidget,
  MotivationalFormWidget,
  PitchRecorderWidget,
  VideoCarouselWidget,
} from './checkup-assistant';

// Shared (generic) widgets
import {
  ChecklistWidget,
  CountrySelectorWidget,
  RatingWidget,
  SliderWidget,
  TimelineSelectorWidget,
} from './shared';

// QultureUp widgets
import { BrainAssessmentWidget } from './qultureup/BrainAssessmentWidget';

// Care Assessment widgets
import {
  CareCheckinPreferencesWidget,
  CareEmotionalBaselineWidget,
  CarePillarSelectorWidget,
} from './care';

// TelePortal Setup widgets
import { RelocationProfilePanel } from './teleportal-setup/RelocationProfilePanel';
import { RelocationKitBuilder } from './teleportal-setup/RelocationKitBuilder';
import { VaultDocumentScanner } from './teleportal-setup/VaultDocumentScanner';
import { HouseholdCaptureWidget } from './teleportal-setup/HouseholdCaptureWidget';
import { SituationCaptureWidget } from './teleportal-setup/SituationCaptureWidget';

// ═══════════════════════════════════════════════════════════════════════
// TYPES
// ═══════════════════════════════════════════════════════════════════════

/** Type-safe widget component that accepts WidgetProps */
export type WidgetComponent = ComponentType<WidgetProps>;

export interface WidgetRegistryEntry {
  /** Canonical widget name (snake_case) */
  name: string;
  /** React component implementing WidgetProps */
  component: WidgetComponent;
  /**
   * Display modes this widget supports.
   *
   * CONTRACT — read before registering a widget without `'blended'`:
   *   'blended' — the widget renders INLINE. The host owns any surrounding
   *               chrome (card, dialog, …).
   *   'modal'   — the widget renders its OWN `<Dialog>` and owns the overlay,
   *               the focus trap and the scroll lock. Hosts must render it
   *               bare.
   *
   * `use-guide-messages.ts` asks for `'blended'` on every feature, so a widget
   * reaches `'modal'` in the guide ONLY by omitting `'blended'` here. That is
   * why `GuideWidgetRenderer` no longer wraps anything: a modal-only widget
   * that did NOT bring its own dialog would render loose on the page, and one
   * that does gets two stacked modals. `registry.spec.ts` pins the modal-only
   * set so neither can be introduced unnoticed.
   */
  displayModes: WidgetDisplayMode[];
  /** Default display mode when none is requested */
  defaultDisplayMode: WidgetDisplayMode;
  /** Alternative names that resolve to this widget */
  aliases?: string[];
  /** Human-readable description for tooling/docs */
  description?: string;
}

// ═══════════════════════════════════════════════════════════════════════
// REGISTRY CLASS
// ═══════════════════════════════════════════════════════════════════════

class WidgetRegistry {
  private widgets: Map<string, WidgetRegistryEntry> = new Map();
  private aliases: Map<string, string> = new Map();

  /**
   * Register a widget definition.
   *
   * Mirrors the registration into the shared `@tonnie/copilot`
   * `widgetRegistry` singleton because `useWidgetState` (and any other
   * @tonnie/copilot consumer) reads sanitization config from there.
   * Without the mirror, every persisted widget state was silently
   * dropped on resume with `unknown_widget` — the journey-web local
   * registry was registered but the engine's lookup hit an empty
   * shared singleton. Observed 2026-04-28: `<VideoCarouselWidget>`
   * lost the `watched` array on every page re-mount, so users had
   * to re-watch all videos every time they returned to the assessment.
   */
  register(entry: WidgetRegistryEntry): void {
    this.widgets.set(entry.name, entry);
    this.aliases.set(entry.name.toLowerCase(), entry.name);
    if (entry.aliases) {
      entry.aliases.forEach((alias) => {
        this.aliases.set(alias.toLowerCase(), entry.name);
      });
    }
    // Mirror into the shared @tonnie/copilot registry so
    // `useWidgetState`'s `sanitizeState` lookup succeeds.
    sharedWidgetRegistry.register({
      name: entry.name,
      component: entry.component,
      displayModes: entry.displayModes,
      defaultDisplayMode: entry.defaultDisplayMode,
      aliases: entry.aliases,
    });
  }

  /**
   * Get widget by name (supports aliases, case-insensitive).
   */
  get(name: string): WidgetRegistryEntry | undefined {
    const normalized = name.toLowerCase();
    const canonical = this.aliases.get(normalized);
    if (!canonical) return undefined;
    return this.widgets.get(canonical);
  }

  /**
   * Check if a widget exists (supports aliases).
   */
  has(name: string): boolean {
    return this.aliases.has(name.toLowerCase());
  }

  /**
   * Get all registered widget names (canonical names only).
   */
  getAllNames(): string[] {
    return Array.from(this.widgets.keys());
  }

  /**
   * Get all registered entries.
   */
  getAll(): WidgetRegistryEntry[] {
    return Array.from(this.widgets.values());
  }

  /**
   * Get the display mode for a widget, respecting the requested mode if supported.
   */
  getDisplayMode(widgetName: string, requestedMode?: WidgetDisplayMode): WidgetDisplayMode {
    const widget = this.get(widgetName);
    if (!widget) return 'modal';
    if (requestedMode && widget.displayModes.includes(requestedMode)) {
      return requestedMode;
    }
    return widget.defaultDisplayMode;
  }

  /**
   * Get the number of registered widgets.
   */
  get size(): number {
    return this.widgets.size;
  }
}

// ═══════════════════════════════════════════════════════════════════════
// SINGLETON INSTANCE — pre-populated
// ═══════════════════════════════════════════════════════════════════════

export const widgetRegistry = new WidgetRegistry();

// ─── CheckUp assessment widgets ───
widgetRegistry.register({
  name: 'video_carousel',
  component: VideoCarouselWidget as WidgetComponent,
  displayModes: ['blended', 'modal'],
  defaultDisplayMode: 'blended',
  aliases: ['videoCarousel'],
  description: 'Video content carousel for onboarding videos',
});

widgetRegistry.register({
  name: 'pitch_recorder',
  component: PitchRecorderWidget as WidgetComponent,
  displayModes: ['modal'],
  defaultDisplayMode: 'modal',
  aliases: ['pitchRecorder', 'pitch'],
  description: 'Video recording widget for talent pitch',
});

widgetRegistry.register({
  name: 'language_test',
  component: LanguageTestWidget as WidgetComponent,
  displayModes: ['blended', 'modal'],
  defaultDisplayMode: 'blended',
  aliases: ['languageTest'],
  description: 'Language proficiency testing (English, Portuguese)',
});

widgetRegistry.register({
  name: 'motivational_form',
  component: MotivationalFormWidget as WidgetComponent,
  displayModes: ['modal'],
  defaultDisplayMode: 'modal',
  aliases: ['motivationalForm'],
  description: 'Motivational assessment questionnaire',
});

// ─── Journey Builder widgets ───
widgetRegistry.register({
  name: 'checklist',
  component: ChecklistWidget as WidgetComponent,
  displayModes: ['blended', 'modal'],
  defaultDisplayMode: 'blended',
  aliases: [
    'checklist_widget',
    'purpose_selector',
    'multi_select',
    'journey_purpose',
    'journeyPurpose',
  ],
  description: 'Multi-select checklist for journey purpose selection',
});

widgetRegistry.register({
  name: 'country_selector',
  component: CountrySelectorWidget as WidgetComponent,
  displayModes: ['blended', 'modal'],
  defaultDisplayMode: 'blended',
  aliases: ['countrySelector', 'country_picker'],
  description: 'Origin/destination country picker',
});

widgetRegistry.register({
  name: 'family_setup',
  component: FamilySetupWidget as WidgetComponent,
  displayModes: ['blended', 'modal'],
  defaultDisplayMode: 'blended',
  aliases: ['familySetup', 'family_config', 'household'],
  description: 'Family configuration (partner, children, family program)',
});

widgetRegistry.register({
  name: 'scenario_selector',
  component: ScenarioSelectorWidget as WidgetComponent,
  displayModes: ['blended', 'modal'],
  defaultDisplayMode: 'blended',
  aliases: ['scenarioSelector', 'scenario_picker', 'journey_scenario'],
  description: 'Journey scenario selection (pre_immigration, already_abroad, etc.)',
});

widgetRegistry.register({
  name: 'journey_summary',
  component: JourneySummaryWidget as WidgetComponent,
  displayModes: ['blended', 'modal'],
  defaultDisplayMode: 'blended',
  aliases: ['journeySummary', 'summary'],
  description: 'Summary display of configured journey',
});

// ─── Generic assessment widgets ───
widgetRegistry.register({
  name: 'rating',
  component: RatingWidget as WidgetComponent,
  displayModes: ['blended', 'modal'],
  defaultDisplayMode: 'blended',
  aliases: ['rating_widget', 'stars', 'score'],
  description: 'Number-based rating selector',
});

widgetRegistry.register({
  name: 'slider',
  component: SliderWidget as WidgetComponent,
  displayModes: ['blended', 'modal'],
  defaultDisplayMode: 'blended',
  aliases: ['slider_widget', 'range'],
  description: 'Range slider for value selection',
});

widgetRegistry.register({
  name: 'timeline_selector',
  component: TimelineSelectorWidget as WidgetComponent,
  displayModes: ['blended', 'modal'],
  defaultDisplayMode: 'blended',
  aliases: ['timeline_selector_widget', 'timeline', 'schedule'],
  description: 'Timeline/date picker for journey planning',
});

// ─── QultureUp Assessment ───
widgetRegistry.register({
  name: 'brain_assessment',
  component: BrainAssessmentWidget as WidgetComponent,
  displayModes: ['blended', 'modal'],
  defaultDisplayMode: 'blended',
  aliases: ['brainAssessment', 'brain_quiz', 'qultureup_assessment'],
  description: 'Brain AI diagnostic quiz widget for QultureUp cultural assessment',
});

// ─── TelePortal Setup ───
widgetRegistry.register({
  name: 'relocation_profile_summary',
  component: RelocationProfilePanel as WidgetComponent,
  displayModes: ['blended', 'modal'],
  defaultDisplayMode: 'blended',
  aliases: ['relocationProfileSummary', 'profile_summary', 'smart_welcome'],
  description: 'Pre-populated relocation profile summary for Setup welcome step',
});

widgetRegistry.register({
  name: 'relocation_kit_builder',
  component: RelocationKitBuilder as WidgetComponent,
  displayModes: ['blended', 'modal'],
  defaultDisplayMode: 'blended',
  aliases: ['relocationKitBuilder', 'kit_builder', 'kit_presentation'],
  description: 'Interactive Relocation Kit review with track/member views and task management',
});

widgetRegistry.register({
  name: 'vault_document_scanner',
  component: VaultDocumentScanner as WidgetComponent,
  displayModes: ['blended', 'modal'],
  defaultDisplayMode: 'blended',
  aliases: ['vaultDocumentScanner', 'vault_scanner', 'document_scanner'],
  description: 'Vault document linking for Setup dimension 6 (existing documents)',
});

widgetRegistry.register({
  name: 'household_capture',
  component: HouseholdCaptureWidget as WidgetComponent,
  displayModes: ['blended', 'modal'],
  defaultDisplayMode: 'blended',
  aliases: ['householdCapture', 'household_setup'],
  description: 'Household member capture with per-member details for TelePortal Setup',
});

widgetRegistry.register({
  name: 'situation_capture',
  component: SituationCaptureWidget as WidgetComponent,
  displayModes: ['blended', 'modal'],
  defaultDisplayMode: 'blended',
  aliases: ['situationCapture', 'legal_housing_professional', 'professional_legal'],
  description: 'Legal, professional, and housing situation capture for TelePortal Setup',
});

// ─── Care Assessment ───
widgetRegistry.register({
  name: 'care_emotional_baseline',
  component: CareEmotionalBaselineWidget as WidgetComponent,
  displayModes: ['blended'],
  defaultDisplayMode: 'blended',
  aliases: ['careEmotionalBaseline', 'emotional_baseline'],
  description: 'Pre-move emotional state capture for Care Assessment',
});

widgetRegistry.register({
  name: 'care_pillar_selector',
  component: CarePillarSelectorWidget as WidgetComponent,
  displayModes: ['blended'],
  defaultDisplayMode: 'blended',
  aliases: ['carePillarSelector', 'pillar_selector'],
  description: 'Pillar prioritization selector for Care Assessment',
});

widgetRegistry.register({
  name: 'care_checkin_preferences',
  component: CareCheckinPreferencesWidget as WidgetComponent,
  displayModes: ['blended'],
  defaultDisplayMode: 'blended',
  aliases: ['careCheckinPreferences', 'checkin_preferences'],
  description: 'Check-in frequency and reminder preferences for Care Assessment',
});
